import { find, isNil, reduce, some } from "lodash";
import numbro from "numbro";
import { assign, createMachine } from "xstate";
import {
    PaymentMethodName,
    PaymentPlanType,
    PaymentRequestStatus,
} from "../../PaymentContainerClient/commons/enums";
import CreatePaymentResponseDto from "../../PaymentContainerClient/dtos/CreatePaymentResponseDto";
import PaymentRequestDto from "../../PaymentContainerClient/dtos/PaymentRequestDto";
import { isInConstraintsRange } from "../../ui/commons/calculators";
import getAllNotPaidNotScheduledNotOnHoldInstallments from "../../ui/commons/getAllNotPaidNotScheduledNotOnHoldInstallments";
import getFirstNotPaidNotScheduledNotOnHoldInstallment from "../../ui/commons/getFirstNotPaidNotScheduledNotOnHoldInstallment";
import getFirstPaymentWithAtLeastOneSelectedInstallments from "../../ui/commons/getFirstPaymentWithAtLeastOneSelectedInstallments";
import getInstallmentById from "../../ui/commons/getInstallmentById";
import { createPayment } from "../usecases/createPayment";
import { updatePayment } from "../usecases/updatePayment";

interface Context {
    currentStep: number;
    isPayNow: boolean;
    paymentPlanId?: string;
    installmentIds: string[];
    paymentMethod?: PaymentMethodName;
    paymentData?: CreatePaymentResponseDto;
    error?: any;
}

export enum PaymentFormMachineState {
    Filling = "filling",
    Submitting = "submitting",
    Success = "success",
}

function initializePaymentPlanId(paymentRequest: PaymentRequestDto): string {
    const singleInstallmentPaymentPlan = find(
        paymentRequest.availablePaymentPlans,
        { type: PaymentPlanType.SingleInstallment }
    );
    const multipleInstallmentsPaymentPlan = find(
        paymentRequest.availablePaymentPlans,
        { type: PaymentPlanType.MultipleInstallments }
    );
    return singleInstallmentPaymentPlan &&
        !some(paymentRequest.payments, {
            paymentPlanId: multipleInstallmentsPaymentPlan?.id,
        })
        ? singleInstallmentPaymentPlan.id
        : multipleInstallmentsPaymentPlan!.id;
}

function initializeInstallmentIds(
    paymentPlanId: string,
    paymentRequest: PaymentRequestDto
): string[] {
    const paymentPlan = find(paymentRequest.availablePaymentPlans, {
        id: paymentPlanId,
    });

    const firstNotPaidNotScheduledNotOnHoldInstallment =
        getFirstNotPaidNotScheduledNotOnHoldInstallment(
            paymentPlan!.installments,
            paymentRequest.payments
        );

    return paymentRequest.status !== PaymentRequestStatus.Paid &&
        paymentRequest.status !== PaymentRequestStatus.Scheduled &&
        // If there is no unpaid installment, no installment will be selected
        firstNotPaidNotScheduledNotOnHoldInstallment
        ? [
              paymentPlan!.type === PaymentPlanType.SingleInstallment
                  ? paymentPlan!.installments[0].id
                  : firstNotPaidNotScheduledNotOnHoldInstallment.id,
          ]
        : [];
}

function getAllTheNotPaidInstallmentIds(
    paymentPlanId: string,
    paymentRequest: PaymentRequestDto
): string[] {
    const paymentPlan = find(paymentRequest.availablePaymentPlans, {
        id: paymentPlanId,
    });

    return getAllNotPaidNotScheduledNotOnHoldInstallments(
        paymentPlan!.installments,
        paymentRequest.payments
    ).map((installment) => installment.id);
}

function recalcPaymentMethod(
    installmentIds: string[],
    paymentRequest: PaymentRequestDto,
    contextPaymentMethod?: PaymentMethodName,
    isPayNow: boolean = true
): PaymentMethodName | undefined {
    const installments = installmentIds.map((selectedInstallmentId) =>
        getInstallmentById(selectedInstallmentId, paymentRequest)
    );
    const tsPayCreditCardPaymentMethod = find(
        paymentRequest.availablePaymentMethods,
        { name: PaymentMethodName.TsPayCreditCard }
    );
    const tsPayPayPalPaymentMethod = find(
        paymentRequest.availablePaymentMethods,
        { name: PaymentMethodName.TsPayPayPal }
    );
    const tsPayBankTransferPaymentMethod = find(
        paymentRequest.availablePaymentMethods,
        { name: PaymentMethodName.TsPayBankTransfer }
    );
    const tsPayInstantBankTransferPaymentMethod = find(
        paymentRequest.availablePaymentMethods,
        { name: PaymentMethodName.TsPayInstantBankTransfer }
    );
    const selectedPaymentMethod = find(paymentRequest.availablePaymentMethods, {
        name: contextPaymentMethod,
    });

    let paymentMethod = undefined;

    if (
        selectedPaymentMethod &&
        isInConstraintsRange(
            reduce(
                installments,
                (result, installment) =>
                    numbro(installment.amount.amount).add(result).value(),
                0
            ).toString(),
            selectedPaymentMethod.constraints
        ) &&
        !(
            !isPayNow &&
            selectedPaymentMethod.name ===
                PaymentMethodName.TsPayInstantBankTransfer
        )
    ) {
        paymentMethod = selectedPaymentMethod.name;
    } else if (
        tsPayCreditCardPaymentMethod &&
        isInConstraintsRange(
            reduce(
                installments,
                (result, installment) =>
                    numbro(installment.amount.amount).add(result).value(),
                0
            ).toString(),
            tsPayCreditCardPaymentMethod.constraints
        )
    ) {
        paymentMethod = PaymentMethodName.TsPayCreditCard;
    } else if (
        tsPayPayPalPaymentMethod &&
        isInConstraintsRange(
            reduce(
                installments,
                (result, installment) =>
                    numbro(installment.amount.amount).add(result).value(),
                0
            ).toString(),
            tsPayPayPalPaymentMethod.constraints
        )
    ) {
        paymentMethod = PaymentMethodName.TsPayPayPal;
    } else if (
        isPayNow &&
        tsPayInstantBankTransferPaymentMethod &&
        isInConstraintsRange(
            reduce(
                installments,
                (result, installment) =>
                    numbro(installment.amount.amount).add(result).value(),
                0
            ).toString(),
            tsPayInstantBankTransferPaymentMethod.constraints
        )
    ) {
        paymentMethod = PaymentMethodName.TsPayInstantBankTransfer;
    } else if (
        tsPayBankTransferPaymentMethod &&
        isInConstraintsRange(
            reduce(
                installments,
                (result, installment) =>
                    numbro(installment.amount.amount).add(result).value(),
                0
            ).toString(),
            tsPayBankTransferPaymentMethod.constraints
        )
    ) {
        paymentMethod = PaymentMethodName.TsPayBankTransfer;
    }

    return paymentMethod;
}

export const paymentFormMachine = createMachine<Context>({
    id: "paymentFormMachine",
    initial: PaymentFormMachineState.Filling,
    context: {
        currentStep: 0,
        isPayNow: true,
        installmentIds: [],
    },
    states: {
        [PaymentFormMachineState.Filling]: {
            on: {
                INITIALIZE: {
                    actions: [
                        assign({
                            isPayNow: (_context, event) =>
                                event.paymentRequest.status !==
                                PaymentRequestStatus.Scheduled,
                        }),
                        assign({
                            paymentPlanId: (_context, event) =>
                                initializePaymentPlanId(event.paymentRequest),
                        }),
                        assign({
                            installmentIds: (context, event) =>
                                initializeInstallmentIds(
                                    context.paymentPlanId!,
                                    event.paymentRequest
                                ),
                        }),
                        assign({
                            paymentMethod: (context, event) =>
                                recalcPaymentMethod(
                                    context.installmentIds,
                                    event.paymentRequest,
                                    context.paymentMethod
                                ),
                        }),
                    ],
                },
                NEXT_STEP: {
                    actions: assign({
                        currentStep: (context, _event) =>
                            context.currentStep < 2
                                ? context.currentStep + 1
                                : context.currentStep,
                    }),
                },
                PREVIOUS_STEP: {
                    actions: assign({
                        currentStep: (context, _event) =>
                            context.currentStep > 0
                                ? context.currentStep - 1
                                : 0,
                    }),
                },
                SET_STEP: {
                    actions: assign({
                        currentStep: (context, event) =>
                            event.step >= 0 && event.step <= 3
                                ? event.step
                                : context.currentStep,
                    }),
                },
                SET_PAY_NOW: {
                    actions: [
                        assign({
                            isPayNow: (_context, event) => event.isPayNow,
                        }),
                        assign({
                            installmentIds: (context, event) =>
                                event.isPayNow
                                    ? initializeInstallmentIds(
                                          context.paymentPlanId!,
                                          event.paymentRequest
                                      )
                                    : getAllTheNotPaidInstallmentIds(
                                          context.paymentPlanId!,
                                          event.paymentRequest
                                      ),
                        }),
                        assign({
                            paymentMethod: (context, event) =>
                                recalcPaymentMethod(
                                    context.installmentIds,
                                    event.paymentRequest,
                                    context.paymentMethod,
                                    event.isPayNow
                                ),
                        }),
                    ],
                },
                SET_PAYMENT_PLAN_ID: {
                    actions: [
                        assign({
                            paymentPlanId: (_context, event) =>
                                event.paymentPlanId,
                        }),
                        assign({
                            installmentIds: (context, event) =>
                                context.isPayNow
                                    ? initializeInstallmentIds(
                                          context.paymentPlanId!,
                                          event.paymentRequest
                                      )
                                    : getAllTheNotPaidInstallmentIds(
                                          context.paymentPlanId!,
                                          event.paymentRequest
                                      ),
                        }),
                    ],
                },
                ADD_INSTALLMENT_ID: {
                    actions: [
                        assign({
                            installmentIds: (context, event) =>
                                context.installmentIds.concat(
                                    event.installmentId
                                ),
                        }),
                        assign({
                            paymentMethod: (context, event) =>
                                recalcPaymentMethod(
                                    context.installmentIds,
                                    event.paymentRequest,
                                    context.paymentMethod
                                ),
                        }),
                    ],
                },
                REMOVE_INSTALLMENT_ID: {
                    actions: [
                        assign({
                            installmentIds: (context, event) =>
                                context.installmentIds.filter(
                                    (installmentId) =>
                                        installmentId !== event.installmentId
                                ),
                        }),
                        assign({
                            paymentMethod: (context, event) =>
                                recalcPaymentMethod(
                                    context.installmentIds,
                                    event.paymentRequest,
                                    context.paymentMethod
                                ),
                        }),
                    ],
                },
                SET_PAYMENT_METHOD: {
                    actions: assign({
                        paymentMethod: (_context, event) => event.paymentMethod,
                    }),
                },
                SUBMIT: PaymentFormMachineState.Submitting,
            },
        },
        [PaymentFormMachineState.Submitting]: {
            invoke: {
                src: (context, event) => {
                    const paymentWithAtLeastOneSelectedInstallments =
                        getFirstPaymentWithAtLeastOneSelectedInstallments(
                            event.paymentRequest.payments,
                            context.installmentIds
                        );
                    return isNil(paymentWithAtLeastOneSelectedInstallments) ||
                        !context.isPayNow
                        ? createPayment(
                              event.token,
                              event.locale,
                              context.paymentPlanId!,
                              context.installmentIds,
                              context.paymentMethod!,
                              !context.isPayNow
                          )
                        : updatePayment(
                              event.token,
                              event.locale,
                              context.paymentPlanId!,
                              context.installmentIds,
                              context.paymentMethod!,
                              paymentWithAtLeastOneSelectedInstallments.id
                          );
                },
                onDone: {
                    target: PaymentFormMachineState.Success,
                    actions: assign({
                        paymentData: (_, event) => event.data,
                    }),
                },
                onError: {
                    target: PaymentFormMachineState.Filling,
                    actions: [
                        assign({
                            error: (_, event) => event.data,
                        }),
                        "notifyErrorCreatingPayment",
                    ],
                },
            },
        },
        [PaymentFormMachineState.Success]: {
            invoke: {
                src: "openPaymentLink",
            },
            on: {
                RESET: {
                    target: PaymentFormMachineState.Filling,
                },
            },
        },
    },
});
